import React, { useEffect, useState, useCallback, useRef } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, doc, updateDoc, addDoc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { uploadImage } from '../firebase';
import { buildEventFields, fetchEvents, getEventLabel } from '../utils/events';
import './MassEditor.css';
import Spinner from './Spinner';
import Notification from './Notification';

const getProductKey = (product) => `${product.collectionName || 'products'}:${product.id}`;

const MassEditor = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ message: '', show: false });
  const [newCategory, setNewCategory] = useState('');
  const [newProduct, setNewProduct] = useState({
    model: '',
    stock: '',
    costPrice: '',
    price: '',
    category: '',
    categories: [],
    description: '',
    images: [],
    hidden: false,
    discountExcluded: false,
    excludeFromStock: false,
    eventId: '',
    eventName: '',
    eventDate: '',
    collectionName: 'products'
  });
  const [isAdmin, setIsAdmin] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStockFilter, setSelectedStockFilter] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [massActionCategories, setMassActionCategories] = useState([]);
  const [massActionDiscount, setMassActionDiscount] = useState('');
  const [massActionMargin, setMassActionMargin] = useState('');
  const [massActionPrice, setMassActionPrice] = useState('');
  const [massActionCostPrice, setMassActionCostPrice] = useState('');
  const [massActionStock, setMassActionStock] = useState('');
  const [massActionEventId, setMassActionEventId] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const fileInputRefs = useRef({});
  const [descriptionModal, setDescriptionModal] = useState({
    isOpen: false,
    productKey: null,
    productModel: '',
    value: ''
  });
  
  // Estado para columnas visibles - todas activas por defecto
  const [visibleColumns, setVisibleColumns] = useState({
    collection: true,
    model: true,
    stock: true,
    costPrice: true,
    price: true,
    category: true,
    description: true,
    images: true,
    hidden: true,
    discountExcluded: true,
    excludeFromStock: true,
    event: true
  });
  const activeEvents = events.filter(event => event.isActive);

  const fetchProducts = useCallback(async () => {
    try {
      const [productsCollection, otherProductsCollection] = await Promise.all([
        getDocs(collection(db, 'products')),
        getDocs(collection(db, 'otherProducts'))
      ]);
      const normalizeProducts = (snapshot, collectionName) => snapshot.docs.map((productDoc) => {
        const data = productDoc.data();
        const categoriesArr = Array.isArray(data.categories) ? data.categories : (data.category ? [data.category] : []);
        const category = data.category || (categoriesArr.length ? categoriesArr[0] : '');
        return {
          ...data,
          id: productDoc.id,
          key: getProductKey({ id: productDoc.id, collectionName }),
          collectionName,
          stock: data.stock ?? 0,
          costPrice: data.costPrice ?? 0,
          price: data.price ?? 0,
          model: data.model || '',
          category,
          categories: categoriesArr,
          description: data.description || '',
          images: data.images || [],
          hidden: data.hidden || false,
          discountExcluded: data.discountExcluded ?? false,
          excludeFromStock: data.excludeFromStock ?? false,
          eventId: data.eventId || '',
          eventName: data.eventName || '',
          eventDate: data.eventDate || ''
        };
      });
      let productsData = [
        ...normalizeProducts(productsCollection, 'products'),
        ...normalizeProducts(otherProductsCollection, 'otherProducts')
      ];
      productsData = productsData.sort((a, b) => a.model.localeCompare(b.model));
      setProducts(productsData);

      const fromCategory = productsData.map(p => p.category).filter(Boolean);
      const fromCategories = productsData.flatMap(p => p.categories || []);
      const uniqueCategories = [...new Set([...fromCategory, ...fromCategories])].sort((a, b) => a.localeCompare(b));
      setCategories(uniqueCategories);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error fetching products:", error);
      setNotification({ message: 'Error al cargar productos.', show: true });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      setEvents(await fetchEvents(db));
    } catch (error) {
      console.error('Error fetching events:', error);
      setNotification({ message: 'Error al cargar eventos.', show: true });
    }
  }, []);

  // Agregar estado para verificar si la autenticación está lista
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    // Verificar estado de autenticación y escuchar cambios
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists) {
            const d = userDoc.data() || {};
            const r = d.role;
            const feats = Array.isArray(d.allowedFeatures) ? d.allowedFeatures : [];
            const ok =
              r === 'admin' ||
              (r === 'staff' && feats.includes('massEditor'));
            setIsAdmin(ok);
          } else {
            setIsAdmin(false);
          }
        } catch (error) {
          console.error("Error checking auth status:", error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setAuthReady(true);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (authReady) {
      fetchProducts();
      loadEvents();
    }
  }, [authReady, fetchProducts, loadEvents]);

  const handleInputChange = (productKey, field, value) => {
    if (!isAdmin && (field === 'stock' || field === 'costPrice')) return;
    setProducts(products.map(product => {
      if (product.key !== productKey) return product;
      if (field === 'category') {
        return { ...product, category: value, categories: value ? [value] : [] };
      }
      return { ...product, [field]: value };
    }));
    setHasUnsavedChanges(true);
  };

  const handleProductCategoryAdd = (productKey, category) => {
    if (!category) return;

    setProducts(products.map(product => {
      if (product.key !== productKey) return product;
      const currentCategories = Array.isArray(product.categories)
        ? product.categories
        : (product.category ? [product.category] : []);
      const nextCategories = currentCategories.includes(category)
        ? currentCategories
        : [...currentCategories, category];

      return {
        ...product,
        category: nextCategories[0] || '',
        categories: nextCategories
      };
    }));
    setHasUnsavedChanges(true);
  };

  const handleProductCategoryRemove = (productKey, category) => {
    setProducts(products.map(product => {
      if (product.key !== productKey) return product;
      const currentCategories = Array.isArray(product.categories)
        ? product.categories
        : (product.category ? [product.category] : []);
      const nextCategories = currentCategories.filter(cat => cat !== category);

      return {
        ...product,
        category: nextCategories[0] || '',
        categories: nextCategories
      };
    }));
    setHasUnsavedChanges(true);
  };

  const handleHiddenToggle = (productKey) => {
    setProducts(products.map(product =>
      product.key === productKey ? { ...product, hidden: !product.hidden } : product
    ));
    setHasUnsavedChanges(true);
  };

  const handleDiscountExcludedToggle = (productKey) => {
    setProducts(products.map(product =>
      product.key === productKey ? { ...product, discountExcluded: !product.discountExcluded } : product
    ));
    setHasUnsavedChanges(true);
  };

  const handleExcludeFromStockToggle = (productKey) => {
    setProducts(products.map(product =>
      product.key === productKey ? { ...product, excludeFromStock: !product.excludeFromStock } : product
    ));
    setHasUnsavedChanges(true);
  };

  const handleProductEventChange = (productKey, eventId) => {
    const selectedEvent = events.find(event => event.id === eventId) || null;
    setProducts(products.map(product =>
      product.key === productKey ? { ...product, ...buildEventFields(selectedEvent) } : product
    ));
    setHasUnsavedChanges(true);
  };

  // Funciones para selección masiva
  const handleSelectAll = (checked) => {
    if (checked) {
      const visibleProductIds = new Set(filteredProducts.map(p => p.key));
      setSelectedProducts(visibleProductIds);
    } else {
      setSelectedProducts(new Set());
    }
  };

  const handleSelectProduct = (productKey, checked) => {
    const newSelected = new Set(selectedProducts);
    if (checked) {
      newSelected.add(productKey);
    } else {
      newSelected.delete(productKey);
    }
    setSelectedProducts(newSelected);
  };

  // Filtrar productos por categoría y stock
  const filteredProducts = products.filter(product => {
    const inCategory = product.category === selectedCategory;
    const inCategories = product.categories && product.categories.includes(selectedCategory);
    const categoryMatch = selectedCategory ? (inCategory || inCategories) : true;
    const modelMatch = modelSearch
      ? (product.model || '').toLowerCase().includes(modelSearch.toLowerCase().trim())
      : true;
    
    // Filtro por stock/visibilidad
    let stockMatch = true;
    if (selectedStockFilter) {
      switch (selectedStockFilter) {
        case 'sin-stock':
          stockMatch = product.stock <= 0;
          break;
        case 'poco-stock':
          stockMatch = product.stock < 10;
          break;
        case 'stock-critico':
          stockMatch = product.stock < 3;
          break;
        case 'ocultas':
          stockMatch = product.hidden === true;
          break;
        case 'no-apto-descuento':
          stockMatch = product.discountExcluded === true;
          break;
        case 'no-computa-stock':
          stockMatch = product.excludeFromStock === true;
          break;
        default:
          stockMatch = true;
      }
    }
    
    return categoryMatch && stockMatch && modelMatch;
  });

  const isAllSelected = filteredProducts.length > 0 && filteredProducts.every(p => selectedProducts.has(p.key));
  const isPartiallySelected = selectedProducts.size > 0 && !isAllSelected;

  // Funciones para acciones masivas
  const handleMassHide = (hide) => {
    if (selectedProducts.size === 0) return;
    
    setProducts(products.map(product =>
      selectedProducts.has(product.key) ? { ...product, hidden: hide } : product
    ));
    setSelectedProducts(new Set());
    setHasUnsavedChanges(true);
    setNotification({ message: `${selectedProducts.size} productos ${hide ? 'ocultados' : 'mostrados'}.`, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 3000);
  };

  const handleMassChangeCategory = () => {
    if (selectedProducts.size === 0 || massActionCategories.length === 0) return;
    const nextCategories = [...massActionCategories];
    setProducts(products.map(product =>
      selectedProducts.has(product.key)
        ? { ...product, category: nextCategories[0] || '', categories: nextCategories }
        : product
    ));
    setSelectedProducts(new Set());
    setMassActionCategories([]);
    setHasUnsavedChanges(true);
    setNotification({ message: `Categorías cambiadas para ${selectedProducts.size} productos.`, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 3000);
  };

  const handleMassCategoryAdd = (category) => {
    if (!category) return;
    setMassActionCategories(prev => prev.includes(category) ? prev : [...prev, category]);
  };

  const handleMassCategoryRemove = (category) => {
    setMassActionCategories(prev => prev.filter(cat => cat !== category));
  };

  const handleMassChangeEvent = () => {
    if (selectedProducts.size === 0) return;
    const selectedEvent = events.find(event => event.id === massActionEventId) || null;

    setProducts(products.map(product =>
      selectedProducts.has(product.key)
        ? { ...product, ...buildEventFields(selectedEvent) }
        : product
    ));
    setSelectedProducts(new Set());
    setMassActionEventId('');
    setHasUnsavedChanges(true);
    setNotification({
      message: selectedEvent
        ? `Evento "${selectedEvent.name}" aplicado a ${selectedProducts.size} productos.`
        : `Evento quitado de ${selectedProducts.size} productos.`,
      show: true
    });
    setTimeout(() => setNotification({ message: '', show: false }), 3000);
  };

  const handleMassDiscount = () => {
    const discount = parseFloat(massActionDiscount);
    if (selectedProducts.size === 0 || !discount || discount <= 0 || discount >= 100) return;
    
    setProducts(products.map(product => {
      if (selectedProducts.has(product.key)) {
        const newPrice = product.price * (1 - discount / 100);
        return { ...product, price: Math.round(newPrice * 100) / 100 };
      }
      return product;
    }));
    setSelectedProducts(new Set());
    setMassActionDiscount('');
    setHasUnsavedChanges(true);
    setNotification({ message: `Descuento del ${discount}% aplicado a ${selectedProducts.size} productos.`, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 3000);
  };

  const handleMassMargin = () => {
    const margin = parseFloat(massActionMargin);
    if (selectedProducts.size === 0 || !margin || margin <= 0) return;
    
    setProducts(products.map(product => {
      if (selectedProducts.has(product.key) && isAdmin) {
        const newPrice = product.costPrice * (1 + margin / 100);
        return { ...product, price: Math.round(newPrice * 100) / 100 };
      }
      return product;
    }));
    setSelectedProducts(new Set());
    setMassActionMargin('');
    setHasUnsavedChanges(true);
    setNotification({ message: `Margen del ${margin}% aplicado a ${selectedProducts.size} productos.`, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 3000);
  };

  const handleMassSetPrice = () => {
    const price = parseFloat(massActionPrice);
    if (selectedProducts.size === 0 || !price || price <= 0) return;
    
    setProducts(products.map(product => {
      if (selectedProducts.has(product.key)) {
        return { ...product, price: price };
      }
      return product;
    }));
    setSelectedProducts(new Set());
    setMassActionPrice('');
    setHasUnsavedChanges(true);
    setNotification({ message: `Precio de venta ${price} aplicado a ${selectedProducts.size} productos.`, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 3000);
  };

  const handleMassSetCostPrice = () => {
    const costPrice = parseFloat(massActionCostPrice);
    if (selectedProducts.size === 0 || !costPrice || costPrice <= 0 || !isAdmin) return;
    
    setProducts(products.map(product => {
      if (selectedProducts.has(product.key)) {
        return { ...product, costPrice: costPrice };
      }
      return product;
    }));
    setSelectedProducts(new Set());
    setMassActionCostPrice('');
    setHasUnsavedChanges(true);
    setNotification({ message: `Precio de costo ${costPrice} aplicado a ${selectedProducts.size} productos.`, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 3000);
  };

  const handleMassSetStock = () => {
    const stock = parseInt(massActionStock);
    if (selectedProducts.size === 0 || stock < 0 || !isAdmin) return;
    
    setProducts(products.map(product => {
      if (selectedProducts.has(product.key)) {
        return { ...product, stock: stock };
      }
      return product;
    }));
    setSelectedProducts(new Set());
    setMassActionStock('');
    setHasUnsavedChanges(true);
    setNotification({ message: `Stock ${stock} aplicado a ${selectedProducts.size} productos.`, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 3000);
  };

  const handleMassDelete = async () => {
    if (selectedProducts.size === 0) return;
    
    const confirmDelete = window.confirm(`¿Estás seguro de que quieres eliminar ${selectedProducts.size} productos? Esta acción no se puede deshacer.`);
    if (!confirmDelete) return;

    setLoading(true);
    try {
      const productsToDelete = products.filter(product => selectedProducts.has(product.key));
      const deletePromises = productsToDelete.map(product =>
        updateDoc(doc(db, product.collectionName || 'products', product.id), { deleted: true })
      );
      await Promise.all(deletePromises);
      
      // Remover productos eliminados del estado local
      setProducts(products.filter(product => !selectedProducts.has(product.key)));
      setSelectedProducts(new Set());
      setNotification({ message: `${selectedProducts.size} productos eliminados.`, show: true });
    } catch (error) {
      console.error("Error deleting products:", error);
      setNotification({ message: 'Error al eliminar productos.', show: true });
    } finally {
      setLoading(false);
      setTimeout(() => setNotification({ message: '', show: false }), 3000);
    }
  };

  const handleNewProductChange = (field, value) => {
    // No permitir cambios en campos de admin si no es admin
    if (!isAdmin && (field === 'stock' || field === 'costPrice')) {
      return;
    }

    if (field === 'category') {
      setNewProduct({ ...newProduct, category: value, categories: value ? [value] : [] });
      return;
    }
    
    setNewProduct({ ...newProduct, [field]: value });
  };

  const handleNewProductCategoryAdd = (category) => {
    if (!category) return;

    setNewProduct(prev => {
      const currentCategories = Array.isArray(prev.categories)
        ? prev.categories
        : (prev.category ? [prev.category] : []);
      const nextCategories = currentCategories.includes(category)
        ? currentCategories
        : [...currentCategories, category];

      return {
        ...prev,
        category: nextCategories[0] || '',
        categories: nextCategories
      };
    });
  };

  const handleNewProductCategoryRemove = (category) => {
    setNewProduct(prev => {
      const currentCategories = Array.isArray(prev.categories)
        ? prev.categories
        : (prev.category ? [prev.category] : []);
      const nextCategories = currentCategories.filter(cat => cat !== category);

      return {
        ...prev,
        category: nextCategories[0] || '',
        categories: nextCategories
      };
    });
  };

  const handleNewProductEventChange = (eventId) => {
    const selectedEvent = events.find(event => event.id === eventId) || null;
    setNewProduct(prev => ({
      ...prev,
      ...buildEventFields(selectedEvent)
    }));
  };

  const handleImageUpload = async (productKey, files) => {
    try {
      const uploadPromises = Array.from(files).map(file => uploadImage(file));
      const imageUrls = await Promise.all(uploadPromises);
      if (productKey) {
        const updatedProducts = products.map(product =>
          product.key === productKey ? { ...product, images: [...product.images, ...imageUrls] } : product
        );
        setProducts(updatedProducts);
        setHasUnsavedChanges(true);
      } else {
        setNewProduct({ ...newProduct, images: [...newProduct.images, ...imageUrls] });
      }
      
      // Limpiar el input después de subir
      if (fileInputRefs.current[productKey || 'new']) {
        fileInputRefs.current[productKey || 'new'].value = '';
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setNotification({ message: 'Error al cargar imagen.', show: true });
    }
  };

  const handleRemoveImage = (productKey, index) => {
    if (productKey) {
      setProducts(products.map(product =>
        product.key === productKey ? { ...product, images: product.images.filter((_, i) => i !== index) } : product
      ));
      setHasUnsavedChanges(true);
    } else {
      setNewProduct({ ...newProduct, images: newProduct.images.filter((_, i) => i !== index) });
    }
  };

  const handleSaveChanges = async () => {
    setLoading(true);
    try {
      const updatePromises = products.map(product => {
        const categories = product.categories?.length ? product.categories : (product.category ? [product.category] : []);
        const category = product.category || (categories[0] || '');
        const updateData = {
          model: product.model,
          price: parseFloat(product.price),
          category,
          categories,
          description: product.description,
          images: product.images,
          hidden: product.hidden,
          discountExcluded: product.discountExcluded ?? false,
          excludeFromStock: product.excludeFromStock ?? false,
          eventId: product.eventId || '',
          eventName: product.eventName || '',
          eventDate: product.eventDate || ''
        };
        if (isAdmin) {
          updateData.costPrice = parseFloat(product.costPrice);
          updateData.stock = parseInt(product.stock);
        }
        return updateDoc(doc(db, product.collectionName || 'products', product.id), updateData);
      });
      await Promise.all(updatePromises);
      setHasUnsavedChanges(false);
      setNotification({ message: 'Productos actualizados con éxito.', show: true });
    } catch (error) {
      console.error("Error updating products:", error);
      setNotification({ message: 'Error al actualizar productos.', show: true });
    } finally {
      setLoading(false);
      setTimeout(() => setNotification({ message: '', show: false }), 3000);
    }
  };

  const handleAddNewProduct = async () => {
    setLoading(true);
    try {
      const finalCategories = Array.isArray(newProduct.categories) && newProduct.categories.length
        ? newProduct.categories
        : (newProduct.category ? [newProduct.category] : []);
      const cat = finalCategories[0] || '';
      const newProductData = {
        model: newProduct.model,
        price: parseFloat(newProduct.price),
        category: cat,
        categories: finalCategories,
        description: newProduct.description,
        images: newProduct.images,
        hidden: newProduct.hidden,
        discountExcluded: newProduct.discountExcluded ?? false,
        excludeFromStock: newProduct.excludeFromStock ?? false,
        eventId: newProduct.eventId || '',
        eventName: newProduct.eventName || '',
        eventDate: newProduct.eventDate || ''
      };
      if (isAdmin) {
        newProductData.costPrice = parseFloat(newProduct.costPrice);
        newProductData.stock = parseInt(newProduct.stock);
      }
      await addDoc(collection(db, newProduct.collectionName || 'products'), newProductData);
      setNotification({ message: 'Producto añadido con éxito.', show: true });
      setNewProduct({
        model: '',
        stock: '',
        costPrice: '',
        price: '',
        category: '',
        categories: [],
        description: '',
        images: [],
        hidden: false,
        discountExcluded: false,
        excludeFromStock: false,
        eventId: '',
        eventName: '',
        eventDate: '',
        collectionName: 'products'
      });
      fetchProducts();
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error adding new product:", error);
      setNotification({ message: 'Error al añadir nuevo producto.', show: true });
    } finally {
      setLoading(false);
      setTimeout(() => setNotification({ message: '', show: false }), 3000);
    }
  };

  const handleAddNewCategory = () => {
    if (newCategory && !categories.includes(newCategory)) {
      const sortedCategories = [...categories, newCategory].sort((a, b) => a.localeCompare(b));
      setCategories(sortedCategories);
      handleNewProductCategoryAdd(newCategory);
      setNewCategory('');
    } else if (newCategory) {
      handleNewProductCategoryAdd(newCategory);
      setNewCategory('');
    }
  };

  const handleNewProductDescriptionChange = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
    setNewProduct({ ...newProduct, description: e.target.value });
  };

  const openDescriptionModal = (product) => {
    setDescriptionModal({
      isOpen: true,
      productKey: product.key,
      productModel: product.model || 'Sin modelo',
      value: product.description || ''
    });
  };

  const closeDescriptionModal = () => {
    setDescriptionModal({
      isOpen: false,
      productKey: null,
      productModel: '',
      value: ''
    });
  };

  const saveDescriptionModal = () => {
    if (!descriptionModal.productKey) return;

    setProducts(products.map(product =>
      product.key === descriptionModal.productKey
        ? { ...product, description: descriptionModal.value }
        : product
    ));
    setHasUnsavedChanges(true);
    closeDescriptionModal();
  };

  useEffect(() => {
    if (!descriptionModal.isOpen) return;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeDescriptionModal();
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        saveDescriptionModal();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [descriptionModal.isOpen, descriptionModal.productKey, descriptionModal.value, products]);

  const handleColumnToggle = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const handleToggleAllColumns = () => {
    const visibleCols = getVisibleColumns();
    const allVisible = visibleCols.every(col => visibleColumns[col.key]);
    const newState = {};
    
    // Inicializar con el estado actual
    Object.keys(visibleColumns).forEach(key => {
      newState[key] = visibleColumns[key];
    });
    
    // Actualizar solo las columnas visibles
    visibleCols.forEach(col => {
      newState[col.key] = !allVisible;
    });
    
    setVisibleColumns(newState);
  };

  // Limpiar selección cuando cambien los filtros
  useEffect(() => {
    setSelectedProducts(new Set());
    // Limpiar también los campos de acciones masivas
    setMassActionCategories([]);
    setMassActionDiscount('');
    setMassActionMargin('');
    setMassActionPrice('');
    setMassActionCostPrice('');
    setMassActionStock('');
    setMassActionEventId('');
  }, [selectedCategory, selectedStockFilter, modelSearch]);

  // Generar clases CSS para móviles según columnas visibles
  const getMobileClasses = () => {
    return Object.entries(visibleColumns)
      .filter(([_, isVisible]) => isVisible)
      .map(([column, _]) => `column-visible-${column}`)
      .join(' ');
  };

  // Definir las columnas disponibles
  const availableColumns = [
    { key: 'collection', label: 'Tipo', alwaysVisible: false },
    { key: 'model', label: 'Modelo', alwaysVisible: false },
    { key: 'stock', label: 'Stock', adminOnly: true },
    { key: 'costPrice', label: 'Precio de Costo', adminOnly: true },
    { key: 'price', label: 'Precio de Venta', alwaysVisible: false },
    { key: 'category', label: 'Categoría', alwaysVisible: false },
    { key: 'description', label: 'Descripción', alwaysVisible: false },
    { key: 'images', label: 'Imágenes', alwaysVisible: false },
    { key: 'hidden', label: 'Ocultados', alwaysVisible: false },
    { key: 'discountExcluded', label: 'Apto descuento', alwaysVisible: false },
    { key: 'excludeFromStock', label: 'Computa stock', alwaysVisible: false },
    { key: 'event', label: 'Evento', alwaysVisible: false }
  ];

  // Filtrar columnas según permisos de admin
  const getVisibleColumns = () => {
    const filtered = availableColumns.filter(col => {
      // Si aún no sabemos si es admin, mostrar todas las columnas
      if (isAdmin === null) return true;
      // Si sabemos el estado de admin, filtrar según corresponda
      return !col.adminOnly || (col.adminOnly && isAdmin);
    });
    return filtered;
  };

  // Obtener etiqueta del filtro de stock
  const getStockFilterLabel = (filter) => {
    const labels = {
      'sin-stock': 'Sin stock',
      'poco-stock': 'Poco stock',
      'stock-critico': 'Stock crítico',
      'ocultas': 'Productos ocultos',
      'no-apto-descuento': 'No apto descuento',
      'no-computa-stock': 'No computa stock'
    };
    return labels[filter] || '';
  };

  if (loading || !authReady) return <Spinner />;

  return (
    <div className={`mass-editor-container ${selectedProducts.size > 0 ? 'has-mass-selection' : ''}`}>
      {/* Floating Save Button fixed to viewport */}
      <button
        onClick={handleSaveChanges}
        className={`floating-save-fab ${hasUnsavedChanges ? 'is-pending' : ''}`}
        title={hasUnsavedChanges ? 'Guardar cambios pendientes' : 'No hay cambios para guardar'}
        disabled={!hasUnsavedChanges || loading}
        aria-disabled={!hasUnsavedChanges || loading}
        aria-label={hasUnsavedChanges ? 'Guardar cambios' : 'Guardar deshabilitado'}
      >
        <span className="icon">💾</span>
        {hasUnsavedChanges ? (loading ? 'Guardando…' : 'Guardar cambios') : 'Guardar'}
      </button>

      <div className="mass-editor-header">
        <h2>Editor Masivo de Productos</h2>
        <Notification message={notification.message} show={notification.show} />
      </div>
      
      <div className="filter-section">
        <div className="filter-container">
          <div className="filters-top-section">
            <div className="category-filter-wrapper">
              <label htmlFor="category-selector">Filtrar por categoría:</label>
              <select
                id="category-selector"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="category-filter-select"
              >
                <option value="">Todas las categorías</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div className="stock-filter-wrapper">
              <label htmlFor="stock-selector">Filtrar por estado:</label>
              <select
                id="stock-selector"
                value={selectedStockFilter}
                onChange={(e) => setSelectedStockFilter(e.target.value)}
                className="stock-filter-select"
              >
                <option value="">Todos los productos</option>
                {isAdmin && (
                  <>
                    <option value="sin-stock">Sin stock (≤ 0)</option>
                    <option value="poco-stock">Poco stock (&lt; 10)</option>
                    <option value="stock-critico">Stock crítico (&lt; 3)</option>
                  </>
                )}
                <option value="ocultas">Productos ocultos</option>
                <option value="no-apto-descuento">No apto descuento</option>
                <option value="no-computa-stock">No computa stock</option>
              </select>
            </div>
          </div>
          
          <div className="column-filter-wrapper">
            <label className="column-filter-label">Columnas visibles:</label>
            <div className="column-checkboxes">
              <button 
                onClick={handleToggleAllColumns}
                className="toggle-all-button"
                type="button"
              >
                {getVisibleColumns().every(col => visibleColumns[col.key]) ? 'Ocultar todas' : 'Mostrar todas'}
              </button>
              {getVisibleColumns().map(col => (
                <label key={col.key} className="column-checkbox-label">
                  <input
                    type="checkbox"
                    checked={visibleColumns[col.key]}
                    onChange={() => handleColumnToggle(col.key)}
                    className="column-checkbox"
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">{col.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {(selectedCategory || selectedStockFilter || modelSearch) && (
        <div className="filter-results">
          <span className="filter-results-text">
            Mostrando {filteredProducts.length} de {products.length} productos
            {selectedCategory && ` • Categoría: ${selectedCategory}`}
            {selectedStockFilter && ` • ${getStockFilterLabel(selectedStockFilter)}`}
            {modelSearch && ` • Búsqueda: "${modelSearch}"`}
          </span>
          <button 
            onClick={() => {
              setSelectedCategory('');
              setSelectedStockFilter('');
              setModelSearch('');
            }}
            className="clear-filters-button"
            type="button"
          >
            Limpiar filtros
          </button>
        </div>
      )}

      <div className={`mass-editor-workspace ${selectedProducts.size > 0 ? 'has-side-panel' : ''}`}>
      {selectedProducts.size > 0 && (
        <div className="mass-actions-bar">
          <div className="mass-actions-header">
            <span className="selected-count">{selectedProducts.size} productos seleccionados</span>
            <button 
              onClick={() => {
                setSelectedProducts(new Set());
                setMassActionCategories([]);
                setMassActionDiscount('');
                setMassActionMargin('');
                setMassActionPrice('');
                setMassActionCostPrice('');
                setMassActionStock('');
        setMassActionEventId('');
              }}
              className="clear-selection-button"
              type="button"
            >
              Limpiar selección
            </button>
          </div>
          
          <div className="mass-actions-content">
            <div className="mass-actions-section mass-actions-section--organization">
              <div className="mass-actions-section-header">
                <h4 className="mass-actions-section-title">Organización y visibilidad</h4>
                <span className="mass-actions-section-badge">Bajo riesgo</span>
              </div>
              <div className="mass-actions-row">
                <div className="mass-action-group">
                  <label>Cambiar categorías:</label>
                  {massActionCategories.length > 0 && (
                    <div className="me-category-chips">
                      {massActionCategories.map(cat => (
                        <span key={cat} className="me-category-chip">
                          {cat}
                          <button
                            type="button"
                            className="me-category-chip-remove"
                            onClick={() => handleMassCategoryRemove(cat)}
                            title="Quitar categoría"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <select
                    value=""
                    onChange={(e) => {
                      handleMassCategoryAdd(e.target.value);
                      e.target.value = '';
                    }}
                    className="mass-action-select"
                  >
                    <option value="">Agregar categoría...</option>
                    {categories
                      .filter(cat => !massActionCategories.includes(cat))
                      .map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <button 
                    onClick={handleMassChangeCategory}
                    disabled={massActionCategories.length === 0}
                    className="mass-action-button primary"
                    type="button"
                  >
                    Aplicar categorías
                  </button>
                </div>

                <div className="mass-action-group">
                  <label>Visibilidad:</label>
                  <button 
                    onClick={() => handleMassHide(true)}
                    className="mass-action-button danger"
                    type="button"
                  >
                    Ocultar
                  </button>
                  <button 
                    onClick={() => handleMassHide(false)}
                    className="mass-action-button success"
                    type="button"
                  >
                    Mostrar
                  </button>
                </div>
                <div className="mass-action-group">
                  <label>Asociar a evento:</label>
                  <select
                    value={massActionEventId}
                    onChange={(e) => setMassActionEventId(e.target.value)}
                    className="mass-action-select"
                  >
                    <option value="">Sin evento asignado</option>
                    {activeEvents.map(event => (
                      <option key={event.id} value={event.id}>
                        {getEventLabel(event)} · activo
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleMassChangeEvent}
                    className="mass-action-button primary"
                    type="button"
                  >
                    Aplicar evento
                  </button>
                </div>
              </div>
            </div>

            <div className="mass-actions-section mass-actions-section--pricing">
              <div className="mass-actions-section-header">
                <h4 className="mass-actions-section-title">Precios y stock</h4>
                <span className="mass-actions-section-badge">Impacto medio</span>
              </div>
              <div className="mass-actions-row">
                <div className="mass-action-group">
                  <label>Establecer precio de venta:</label>
                  <input
                    type="number"
                    step="0.01"
                    value={massActionPrice}
                    onChange={(e) => setMassActionPrice(e.target.value)}
                    placeholder="99.99"
                    min="0.01"
                    className="mass-action-input"
                  />
                  <button 
                    onClick={handleMassSetPrice}
                    disabled={!massActionPrice}
                    className="mass-action-button primary"
                    type="button"
                  >
                    Aplicar Precio
                  </button>
                </div>

                {isAdmin && (
                  <>
                    <div className="mass-action-group">
                      <label>Establecer precio de costo:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={massActionCostPrice}
                        onChange={(e) => setMassActionCostPrice(e.target.value)}
                        placeholder="50.00"
                        min="0.01"
                        className="mass-action-input"
                      />
                      <button 
                        onClick={handleMassSetCostPrice}
                        disabled={!massActionCostPrice}
                        className="mass-action-button primary"
                        type="button"
                      >
                        Aplicar Costo
                      </button>
                    </div>

                    <div className="mass-action-group">
                      <label>Establecer stock:</label>
                      <input
                        type="number"
                        value={massActionStock}
                        onChange={(e) => setMassActionStock(e.target.value)}
                        placeholder="100"
                        min="0"
                        className="mass-action-input"
                      />
                      <button 
                        onClick={handleMassSetStock}
                        disabled={massActionStock === ''}
                        className="mass-action-button primary"
                        type="button"
                      >
                        Aplicar Stock
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="mass-actions-section mass-actions-section--promotions">
              <div className="mass-actions-section-header">
                <h4 className="mass-actions-section-title">Promociones y ajustes</h4>
                <span className="mass-actions-section-badge">Revisar antes de aplicar</span>
              </div>
              <div className="mass-actions-row">
                <div className="mass-action-group">
                  <label>Descuento (%):</label>
                  <input
                    type="number"
                    value={massActionDiscount}
                    onChange={(e) => setMassActionDiscount(e.target.value)}
                    placeholder="10"
                    min="1"
                    max="99"
                    className="mass-action-input"
                  />
                  <button 
                    onClick={handleMassDiscount}
                    disabled={!massActionDiscount}
                    className="mass-action-button primary"
                    type="button"
                  >
                    Aplicar
                  </button>
                </div>

                {isAdmin && (
                  <div className="mass-action-group">
                    <label>Margen (%):</label>
                    <input
                      type="number"
                      value={massActionMargin}
                      onChange={(e) => setMassActionMargin(e.target.value)}
                      placeholder="40"
                      min="1"
                      className="mass-action-input"
                    />
                    <button 
                      onClick={handleMassMargin}
                      disabled={!massActionMargin}
                      className="mass-action-button primary"
                      type="button"
                    >
                      Aplicar
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="mass-actions-section mass-actions-section--critical">
              <div className="mass-actions-section-header">
                <h4 className="mass-actions-section-title">Acciones críticas</h4>
                <span className="mass-actions-section-badge danger">Alto riesgo</span>
              </div>
              <div className="mass-actions-row mass-actions-row--critical">
                <div className="mass-action-group mass-action-group--critical">
                  <label>Eliminación masiva:</label>
                  <button 
                    onClick={handleMassDelete}
                    className="mass-action-button danger-outline"
                    type="button"
                  >
                    Eliminar seleccionados
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

        <div className="mass-editor-workspace-content">
          {hasUnsavedChanges && (
            <div className="unsaved-changes-banner">
              <div className="unsaved-changes-content">
                <div className="unsaved-changes-icon">⚠️</div>
                <div className="unsaved-changes-text">
                  <span className="unsaved-changes-title">Cambios pendientes</span>
                  <span className="unsaved-changes-subtitle">Tienes modificaciones sin guardar. Recuerda hacer clic en "Guardar Cambios" para aplicarlos.</span>
                </div>
              </div>
            </div>
          )}

      <div className="table-section">
        <div className="table-admin-toolbar">
          <div className="table-admin-toolbar-left">
            <label htmlFor="model-search-input">Buscar por modelo</label>
            <div className="table-admin-search-group">
              <input
                id="model-search-input"
                type="text"
                value={modelSearch}
                onChange={(e) => setModelSearch(e.target.value)}
                placeholder="Ej: Velocity, Gold, N°5..."
                className="table-admin-search-input"
              />
              {modelSearch && (
                <button
                  type="button"
                  className="table-admin-search-clear"
                  onClick={() => setModelSearch('')}
                >
                  Limpiar
                </button>
              )}
            </div>
          </div>
          <div className="table-admin-toolbar-right">
            <span className="table-admin-chip">Visibles: {filteredProducts.length}</span>
            <span className="table-admin-chip">Seleccionados: {selectedProducts.size}</span>
          </div>
        </div>
        <div className={`table-container ${getMobileClasses()}`}>
          {Object.values(visibleColumns).some(v => v) ? (
            <table className="mass-editor-table">
            <thead>
              <tr>
                <th className="selection-column">
                  <label className="select-all-label">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isPartiallySelected;
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="select-all-checkbox"
                    />
                    <span className="select-all-custom"></span>
                  </label>
                </th>
                {visibleColumns.collection && <th>Tipo</th>}
                {visibleColumns.model && <th className="model-column">Modelo</th>}
                {isAdmin && visibleColumns.stock && <th>Stock</th>}
                {isAdmin && visibleColumns.costPrice && <th>Precio de Costo</th>}
                {visibleColumns.price && <th>Precio de Venta</th>}
                {visibleColumns.category && <th className="category-column">Categoría</th>}
                {visibleColumns.description && <th className="description-column">Descripción</th>}
                {visibleColumns.images && <th>Imágenes</th>}
                {visibleColumns.hidden && <th>Ocultados</th>}
                {visibleColumns.discountExcluded && <th>Apto descuento</th>}
                {visibleColumns.excludeFromStock && <th>Computa stock</th>}
                {visibleColumns.event && <th>Evento</th>}
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map(product => (
                <tr key={product.key} className={selectedProducts.has(product.key) ? 'selected-row' : ''}>
                  <td data-label="Seleccionar" className="selection-cell">
                    <label className="product-select-label">
                      <input
                        type="checkbox"
                        checked={selectedProducts.has(product.key)}
                        onChange={(e) => handleSelectProduct(product.key, e.target.checked)}
                        className="product-select-checkbox"
                      />
                      <span className="product-select-custom"></span>
                    </label>
                  </td>
                  {visibleColumns.collection && (
                    <td data-label="Tipo" className="collection-cell">
                      <span className={`collection-badge collection-badge--${product.collectionName}`}>
                        {product.collectionName === 'otherProducts' ? 'Consumo' : 'Entrada'}
                      </span>
                    </td>
                  )}
                  {visibleColumns.model && (
                    <td data-label="Modelo" className="model-cell">
                      <input
                        type="text"
                        value={product.model}
                        onChange={(e) => handleInputChange(product.key, 'model', e.target.value)}
                        className="table-input model-input"
                        title={product.model || 'Sin modelo'}
                      />
                    </td>
                  )}
                  {isAdmin && visibleColumns.stock && (
                    <td data-label="Stock">
                      <input
                        type="number"
                        value={product.stock}
                        onChange={(e) => handleInputChange(product.key, 'stock', e.target.value)}
                        className="table-input number-input"
                      />
                    </td>
                  )}
                  {isAdmin && visibleColumns.costPrice && (
                    <td data-label="Precio de Costo">
                      <input
                        type="number"
                        step="0.01"
                        value={product.costPrice}
                        onChange={(e) => handleInputChange(product.key, 'costPrice', e.target.value)}
                        className="table-input number-input"
                      />
                    </td>
                  )}
                  {visibleColumns.price && (
                    <td data-label="Precio de Venta">
                      <input
                        type="number"
                        step="0.01"
                        value={product.price}
                        onChange={(e) => handleInputChange(product.key, 'price', e.target.value)}
                        className="table-input number-input"
                      />
                    </td>
                  )}
                  {visibleColumns.category && (
                    <td data-label="Categoría" className="category-cell">
                      <div className="me-category-cell-inner">
                        {product.categories?.length > 0 && (
                          <div className="me-category-chips">
                            {product.categories.map(cat => (
                              <span key={cat} className="me-category-chip">
                                {cat}
                                <button
                                  type="button"
                                  className="me-category-chip-remove"
                                  onClick={() => handleProductCategoryRemove(product.key, cat)}
                                  title="Quitar categoría"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <select
                          value=""
                          onChange={(e) => {
                            handleProductCategoryAdd(product.key, e.target.value);
                            e.target.value = '';
                          }}
                          className="table-select"
                          title={product.categories?.length ? product.categories.join(', ') : ''}
                        >
                          <option value="">Agregar categoría...</option>
                          {categories
                            .filter(cat => !(product.categories || []).includes(cat))
                            .map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                  )}
                  {visibleColumns.description && (
                    <td data-label="Descripción" className="description-cell-col">
                      <div className="description-cell">
                        <div className="description-preview" title={product.description || 'Sin descripción'}>
                          {product.description?.trim() ? product.description : 'Sin descripción'}
                        </div>
                        <button
                          type="button"
                          className="description-edit-button"
                          onClick={() => openDescriptionModal(product)}
                        >
                          Editar
                        </button>
                      </div>
                    </td>
                  )}
                  {visibleColumns.images && (
                    <td data-label="Imágenes" className="images-cell">
                        <div className="me-image-upload-container">
                        <input
                          type="file"
                          onChange={(e) => handleImageUpload(product.key, e.target.files)}
                          multiple
                          accept="image/*"
                            className="mass-editor-file-input"
                          ref={(el) => fileInputRefs.current[product.key] = el}
                        />
                        <div className="me-images-grid">
                          {product.images && product.images.map((image, index) => (
                            <div key={index} className="me-image-preview-wrapper">
                              <img src={image} alt={`${product.model} - ${index + 1}`} className="me-image-preview" />
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(product.key, index)}
                                className="me-remove-image-button"
                                title="Eliminar imagen"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  )}
                  {visibleColumns.hidden && (
                    <td data-label="Oculto" className="hidden-cell">
                      <div className={`hidden-visibility-container ${product.hidden ? 'is-hidden' : 'is-visible'}`}>
                        <button
                          type="button"
                          onClick={() => handleHiddenToggle(product.key)}
                          className={`hidden-visibility-button ${product.hidden ? 'is-hidden' : 'is-visible'}`}
                          aria-label={product.hidden ? 'Marcar como visible' : 'Marcar como oculto'}
                          title={product.hidden ? 'Producto oculto' : 'Producto visible'}
                        >
                          <span className="hidden-visibility-icon" aria-hidden="true">
                            {product.hidden ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 3l18 18" />
                                <path d="M10.58 10.58a2 2 0 0 0 2.83 2.83" />
                                <path d="M9.88 5.09A10.94 10.94 0 0 1 12 5c5 0 9.27 3.11 11 7-1.04 2.34-2.8 4.29-5 5.43" />
                                <path d="M6.61 6.61C4.62 7.88 3.1 9.77 2 12c.73 1.64 1.86 3.1 3.29 4.27" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            )}
                          </span>
                          <span className="hidden-visibility-text">
                            {product.hidden ? 'Oculto' : 'Visible'}
                          </span>
                        </button>
                      </div>
                    </td>
                  )}
                  {visibleColumns.discountExcluded && (
                    <td data-label="Apto descuento" className="hidden-cell">
                      <div className={`hidden-checkbox-container ${(product.discountExcluded ?? false) ? 'is-negative' : ''}`}>
                        <label className="hidden-checkbox-label">
                          <input
                            type="checkbox"
                            checked={!(product.discountExcluded ?? false)}
                            onChange={() => handleDiscountExcludedToggle(product.key)}
                            className="hidden-checkbox"
                          />
                          <span className="hidden-checkbox-custom"></span>
                          <span className="hidden-checkbox-text">
                            {`Descuento: ${!(product.discountExcluded ?? false) ? 'Sí' : 'No'}`}
                          </span>
                        </label>
                      </div>
                    </td>
                  )}
                  {visibleColumns.excludeFromStock && (
                    <td data-label="Computa stock" className="hidden-cell">
                      <div className={`hidden-checkbox-container ${(product.excludeFromStock ?? false) ? 'is-negative' : ''}`}>
                        <label className="hidden-checkbox-label">
                          <input
                            type="checkbox"
                            checked={!(product.excludeFromStock ?? false)}
                            onChange={() => handleExcludeFromStockToggle(product.key)}
                            className="hidden-checkbox"
                          />
                          <span className="hidden-checkbox-custom"></span>
                          <span className="hidden-checkbox-text">
                            {`Stock: ${!(product.excludeFromStock ?? false) ? 'Sí' : 'No'}`}
                          </span>
                        </label>
                      </div>
                    </td>
                  )}
                  {visibleColumns.event && (
                    <td data-label="Evento" className="event-cell">
                      <select
                        value={product.eventId || ''}
                        onChange={(e) => handleProductEventChange(product.key, e.target.value)}
                        className="table-select"
                        title={product.eventName || 'Sin evento asignado'}
                      >
                        <option value="">Sin evento</option>
                        {product.eventId && !activeEvents.some(event => event.id === product.eventId) && (
                          <option value={product.eventId} disabled>
                            {product.eventName || 'Evento inactivo'} · inactivo
                          </option>
                        )}
                        {activeEvents.map(event => (
                          <option key={event.id} value={event.id}>
                            {getEventLabel(event)} · activo
                          </option>
                        ))}
                      </select>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          ) : (
            <div className="no-columns-message">
              No hay columnas seleccionadas. Por favor, selecciona al menos una columna para ver los productos.
            </div>
          )}
        </div>
        
        {/* Floating Save FAB (rendered once, stays visible) */}
      </div>
        </div>
      </div>

      <div className="new-product-section">
        <h3>Añadir Nuevo Producto</h3>
        <div className="new-product-form">
          <div className="form-row">
            <select
              value={newProduct.collectionName}
              onChange={(e) => handleNewProductChange('collectionName', e.target.value)}
              className="form-select"
            >
              <option value="products">Entrada / producto de evento</option>
              <option value="otherProducts">Consumo interno</option>
            </select>
          </div>
          <div className="form-row">
            <input
              type="text"
              placeholder="Modelo del producto"
              value={newProduct.model}
              onChange={(e) => handleNewProductChange('model', e.target.value)}
              className="form-input"
            />
            <input
              type="number"
              step="0.01"
              placeholder="Precio de Venta"
              value={newProduct.price}
              onChange={(e) => handleNewProductChange('price', e.target.value)}
              className="form-input"
            />
          </div>

          {isAdmin && (
            <div className="form-row">
              <input
                type="number"
                placeholder="Stock"
                value={newProduct.stock}
                onChange={(e) => handleNewProductChange('stock', e.target.value)}
                className="form-input"
              />
              <input
                type="number"
                step="0.01"
                placeholder="Precio de Costo"
                value={newProduct.costPrice}
                onChange={(e) => handleNewProductChange('costPrice', e.target.value)}
                className="form-input"
              />
            </div>
          )}

          <div className="form-row">
            <div className="new-product-categories-group">
              <label className="new-product-categories-label">Categorías</label>
              {(newProduct.categories || []).length > 0 && (
                <div className="me-category-chips">
                  {newProduct.categories.map(cat => (
                    <span key={cat} className="me-category-chip">
                      {cat}
                      <button
                        type="button"
                        className="me-category-chip-remove"
                        onClick={() => handleNewProductCategoryRemove(cat)}
                        title="Quitar categoría"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <select
                value=""
                onChange={(e) => {
                  handleNewProductCategoryAdd(e.target.value);
                  e.target.value = '';
                }}
                className="form-select"
              >
                <option value="">Agregar categoría...</option>
                {categories
                  .filter(cat => !(newProduct.categories || []).includes(cat))
                  .map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="category-input-group">
              <input
                type="text"
                placeholder="Nueva Categoría"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="form-input"
              />
              <button 
                onClick={handleAddNewCategory} 
                className="add-category-button"
                type="button"
              >
                Añadir
              </button>
            </div>
          </div>

          <div className="form-row">
            <div className="new-product-event-group">
              <label className="new-product-categories-label">Evento asociado</label>
              <select
                value={newProduct.eventId || ''}
                onChange={(e) => handleNewProductEventChange(e.target.value)}
                className="form-select"
              >
                <option value="">Sin evento asignado</option>
                {activeEvents.map(event => (
                  <option key={event.id} value={event.id}>
                    {getEventLabel(event)} · activo
                  </option>
                ))}
              </select>
            </div>
          </div>

          <textarea
            placeholder="Descripción del producto (máximo 800 caracteres)"
            value={newProduct.description}
            onChange={handleNewProductDescriptionChange}
            maxLength={800}
            className="form-textarea"
            rows="4"
          />

          <div className="form-row">
            <div className={`hidden-form-group ${newProduct.hidden ? 'is-negative' : ''}`}>
              <label className="hidden-form-label">
                <input
                  type="checkbox"
                  checked={newProduct.hidden}
                  onChange={(e) => handleNewProductChange('hidden', e.target.checked)}
                  className="hidden-form-checkbox"
                />
                <span className="hidden-form-checkbox-custom"></span>
                <span className="hidden-form-text">Producto oculto</span>
              </label>
            </div>
            <div className={`hidden-form-group ${(newProduct.discountExcluded ?? false) ? 'is-negative' : ''}`}>
              <label className="hidden-form-label">
                <input
                  type="checkbox"
                  checked={!(newProduct.discountExcluded ?? false)}
                  onChange={(e) => handleNewProductChange('discountExcluded', !e.target.checked)}
                  className="hidden-form-checkbox"
                />
                <span className="hidden-form-checkbox-custom"></span>
                <span className="hidden-form-text">Apto descuento</span>
              </label>
            </div>
            <div className={`hidden-form-group ${(newProduct.excludeFromStock ?? false) ? 'is-negative' : ''}`}>
              <label className="hidden-form-label">
                <input
                  type="checkbox"
                  checked={!(newProduct.excludeFromStock ?? false)}
                  onChange={(e) => handleNewProductChange('excludeFromStock', !e.target.checked)}
                  className="hidden-form-checkbox"
                />
                <span className="hidden-form-checkbox-custom"></span>
                <span className="hidden-form-text">Computa stock</span>
              </label>
            </div>
          </div>

          <div className="file-upload-section">
            <label className="me-file-upload-label">
              <input
                type="file"
                onChange={(e) => handleImageUpload(null, e.target.files)}
                multiple
                accept="image/*"
                className="mass-editor-file-input-hidden"
                ref={(el) => fileInputRefs.current['new'] = el}
              />
              <span className="me-file-upload-button">Seleccionar Imágenes</span>
            </label>
            
            {newProduct.images.length > 0 && (
              <div className="me-images-grid">
                {newProduct.images.map((image, index) => (
                  <div key={index} className="me-image-preview-wrapper">
                    <img src={image} alt={`Nuevo producto - ${index + 1}`} className="me-image-preview" />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(null, index)}
                      className="me-remove-image-button"
                      title="Eliminar imagen"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button onClick={handleAddNewProduct} className="add-product-button">
            Añadir Producto
          </button>
        </div>
      </div>

      {descriptionModal.isOpen && (
        <div className="description-modal-overlay" onClick={closeDescriptionModal}>
          <div className="description-modal" onClick={(e) => e.stopPropagation()}>
            <div className="description-modal-header">
              <h4>Editar descripción</h4>
              <button
                type="button"
                className="description-modal-close"
                onClick={closeDescriptionModal}
                aria-label="Cerrar editor de descripción"
              >
                ×
              </button>
            </div>
            <p className="description-modal-subtitle">
              Producto: <strong>{descriptionModal.productModel}</strong>
            </p>
            <textarea
              value={descriptionModal.value}
              onChange={(e) => setDescriptionModal(prev => ({ ...prev, value: e.target.value }))}
              maxLength={500}
              className="description-modal-textarea"
              rows="10"
              autoFocus
            />
            <div className="description-modal-footer">
              <span className="description-modal-count">{descriptionModal.value.length}/500</span>
              <div className="description-modal-actions">
                <button
                  type="button"
                  className="description-modal-button secondary"
                  onClick={closeDescriptionModal}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="description-modal-button primary"
                  onClick={saveDescriptionModal}
                >
                  Guardar descripción
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MassEditor;